import React from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Package, MapPin, Clock, TrendingUp, AlertTriangle, User, Calendar } from 'lucide-react';
import { MOCK_ASSETS, ASSET_TYPES, AGING_THRESHOLDS, CUSTOMERS } from '../services/mockData';
import '../styles/AssetDetail.css';

const AssetDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const asset = MOCK_ASSETS.find(a => a.id === id);

  if (!asset) {
    return (
      <div className="asset-detail-page">
        <div className="not-found">
          <Package size={64} />
          <h2>Asset Not Found</h2>
          <p>The asset with ID "{id}" could not be found.</p>
          <button onClick={() => navigate('/assets')} className="back-button">
            <ArrowLeft size={18} />
            Back to Assets
          </button>
        </div>
      </div>
    );
  }

  const getStatusBadgeClass = (status) => {
    const statusMap = {
      'AT_WAREHOUSE': 'status-warehouse',
      'IN_TRANSIT': 'status-transit',
      'AT_CUSTOMER': 'status-customer',
      'LOADING': 'status-loading',
      'UNLOADING': 'status-unloading',
      'MAINTENANCE': 'status-maintenance'
    };
    return statusMap[status] || 'status-default';
  };

  const calculateDuration = (transferIn, transferOut) => {
    if (!transferOut) return 'Current';
    const days = Math.floor((new Date(transferOut) - new Date(transferIn)) / (1000 * 60 * 60 * 24));
    return `${days} day${days !== 1 ? 's' : ''}`;
  };

  return (
    <div className="asset-detail-page">
      <div className="page-header">
        <button onClick={() => navigate('/assets')} className="back-button">
          <ArrowLeft size={18} />
          Back to Assets
        </button>
        <div className="header-content">
          <h1>{asset.name}</h1>
          <p className="asset-id">ID: {asset.id}</p>
        </div>
      </div>

      {/* Asset Overview Cards */}
      <div className="overview-grid">
        <div className="info-card">
          <div className="info-icon">
            <Package size={24} />
          </div>
          <div className="info-content">
            <p className="info-label">Asset Type</p>
            <p className="info-value">{asset.type}</p>
            <p className="info-meta">{ASSET_TYPES[asset.type]}</p>
          </div>
        </div>

        <div className="info-card">
          <div className="info-icon">
            <MapPin size={24} />
          </div>
          <div className="info-content">
            <p className="info-label">Current Location</p>
            <p className="info-value">
              {asset.currentWarehouse || asset.currentCustomer || 'In Transit'}
            </p>
            <p className="info-meta">
              <span className={`status-badge ${getStatusBadgeClass(asset.status)}`}>
                {asset.status.replace('_', ' ')}
              </span>
            </p>
          </div>
        </div>

        <div className="info-card">
          <div className="info-icon">
            <Clock size={24} />
          </div>
          <div className="info-content">
            <p className="info-label">Age at Location</p>
            <p className={`info-value ${asset.exceedsThreshold ? 'text-danger' : ''}`}>
              {asset.currentAgeDays !== undefined ? `${asset.currentAgeDays} days` : 'N/A'}
            </p>
            <p className="info-meta">
              {asset.exceedsThreshold ? (
                <span className="threshold-exceeded">
                  <AlertTriangle size={16} />
                  Exceeds threshold ({AGING_THRESHOLDS[asset.type]} days)
                </span>
              ) : (
                `Max: ${AGING_THRESHOLDS[asset.type]} days`
              )}
            </p>
          </div>
        </div>

        <div className="info-card">
          <div className="info-icon">
            <TrendingUp size={24} />
          </div>
          <div className="info-content">
            <p className="info-label">Last Cycle Time</p>
            <p className="info-value">
              {asset.lastCycleTime > 0 ? `${asset.lastCycleTime} days` : 'At Origin'}
            </p>
            <p className="info-meta">Origin: {asset.originWarehouse}</p>
          </div>
        </div>

        {asset.currentCustomer && (
          <div className="info-card">
            <div className="info-icon">
              <User size={24} />
            </div>
            <div className="info-content">
              <p className="info-label">Current Customer</p>
              <p className="info-value">{asset.currentCustomer}</p>
              <p className="info-meta">
                {Object.values(CUSTOMERS).find(c => c.name === asset.currentCustomer)?.id || ''}
              </p>
            </div>
          </div>
        )}

        <div className="info-card">
          <div className="info-icon">
            <Calendar size={24} />
          </div>
          <div className="info-content">
            <p className="info-label">Total Trips</p>
            <p className="info-value">{asset.totalTrips}</p>
            <p className="info-meta">Lifetime movements</p>
          </div>
        </div>
      </div>

      {/* Transfer History */}
      <div className="detail-card">
        <div className="card-header">
          <h2>
            <Clock size={20} />
            Transfer History
          </h2>
          <p className="card-subtitle">Complete warehouse transfer timeline</p>
        </div>
        
        <div className="transfer-history-table-container">
          <table className="transfer-history-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Warehouse</th>
                <th>Transfer In</th>
                <th>Transfer Out</th>
                <th>Age (Days)</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {asset.transferHistory && asset.transferHistory.length > 0 ? (
                asset.transferHistory.map((transfer, index) => {
                  const isCurrentLocation = !transfer.transferOut;
                  const exceedsThreshold = isCurrentLocation && 
                    transfer.ageDays > AGING_THRESHOLDS[asset.type];
                  
                  return (
                    <tr key={index} className={isCurrentLocation ? 'current-location' : ''}>
                      <td>{index + 1}</td>
                      <td>
                        <strong>{transfer.warehouse}</strong>
                        {isCurrentLocation && (
                          <span className="current-badge">Current</span>
                        )}
                      </td>
                      <td>
                        {new Date(transfer.transferIn).toLocaleString()}
                      </td>
                      <td>
                        {transfer.transferOut ? (
                          new Date(transfer.transferOut).toLocaleString()
                        ) : (
                          <span className="text-muted">-</span>
                        )}
                      </td>
                      <td>
                        <span className={exceedsThreshold ? 'age-exceeded' : ''}>
                          {transfer.ageDays}
                          {exceedsThreshold && (
                            <AlertTriangle size={14} color="#ef4444" style={{ marginLeft: '6px' }} />
                          )}
                        </span>
                      </td>
                      <td>
                        {isCurrentLocation ? (
                          <span className="status-badge status-warehouse">At Warehouse</span>
                        ) : (
                          <span className="status-badge status-completed">Completed</span>
                        )}
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="6" className="no-data">No transfer history available</td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Transfer Summary */}
      <div className="summary-grid">
        <div className="summary-card">
          <h3>Transfer Statistics</h3>
          <div className="summary-stats">
            <div className="summary-stat-item">
              <span className="stat-label">Total Transfers:</span>
              <span className="stat-value">{asset.transferHistory?.length || 0}</span>
            </div>
            <div className="summary-stat-item">
              <span className="stat-label">Average Age per Location:</span>
              <span className="stat-value">
                {asset.transferHistory && asset.transferHistory.length > 0
                  ? Math.round(
                      asset.transferHistory.reduce((sum, t) => sum + t.ageDays, 0) / 
                      asset.transferHistory.length
                    )
                  : 0} days
              </span>
            </div>
            <div className="summary-stat-item">
              <span className="stat-label">Origin Warehouse:</span>
              <span className="stat-value">{asset.originWarehouse}</span>
            </div>
          </div>
        </div>

        {asset.exceedsThreshold && (
          <div className="summary-card alert-card">
            <h3>
              <AlertTriangle size={20} />
              Aging Alert
            </h3>
            <div className="alert-content">
              <p>This asset has exceeded the aging threshold at {asset.currentWarehouse}.</p>
              <div className="alert-details">
                <div className="alert-detail-item">
                  <span>Current Age:</span>
                  <strong>{asset.currentAgeDays} days</strong>
                </div>
                <div className="alert-detail-item">
                  <span>Threshold:</span>
                  <strong>{AGING_THRESHOLDS[asset.type]} days</strong>
                </div>
                <div className="alert-detail-item">
                  <span>Exceeded By:</span>
                  <strong className="text-danger">
                    {asset.currentAgeDays - AGING_THRESHOLDS[asset.type]} days
                  </strong>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AssetDetail;

